import { RefreshCw, X } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";

/**
 * Floating banner that appears when a new service worker has installed and is
 * ready to take over. Tapping "Reload" posts SKIP_WAITING + reloads the tab;
 * tapping ✕ dismisses until the next SW update.
 *
 * Paired with `registerType: "prompt"` in vite.config.ts — without it the SW
 * would auto-activate and users wouldn't see this banner at all.
 */
export function PWAUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(err) {
      // Don't spam the console in dev — `registerSW` errors are common during
      // HMR. Real failures will still surface in prod DevTools.
      if (import.meta.env.PROD) console.error("SW register error", err);
    },
    // iOS standalone PWAs don't reliably trigger a SW update check when the
    // app resumes from background — only on a true cold launch. Without this
    // hook, a user who backgrounds the app for hours and re-foregrounds would
    // never see an update banner unless we get lucky with the reload moment.
    //
    // Manually pinging `registration.update()` on `visibilitychange` bridges
    // the gap: each time the doc becomes visible, browser checks the server
    // for a fresh sw.js. Cheap (HEAD-equivalent + 304 short-circuit when
    // unchanged) and well-behaved across all browsers — the no-op fallback
    // path is what desktop already does on tab focus automatically.
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const recheck = () => {
        if (document.visibilityState === "visible") {
          // `update()` returns a promise; awaiting it isn't useful here —
          // any newly-installed SW will trigger our `useRegisterSW` listener
          // independently. Swallow the rejection (network errors offline).
          registration.update().catch(() => undefined);
        }
      };
      document.addEventListener("visibilitychange", recheck);
      // Listener attaches once per page lifetime — no cleanup needed since
      // it lives as long as the document does.
    },
  });
  const t = useT();

  if (!needRefresh) return null;

  return (
    <div
      className="fixed inset-x-0 z-50 px-4"
      style={{
        // Sit just above the bottom nav (72px) + the iOS safe-area inset.
        bottom: "calc(72px + env(safe-area-inset-bottom) + 12px)",
      }}
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-[480px] items-center gap-3 rounded-[var(--r-lg)] border border-border bg-surface px-4 py-3 shadow-[var(--shadow-md)]">
        <div
          className="flex size-9 shrink-0 items-center justify-center rounded-full"
          style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
        >
          <RefreshCw className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold">{t("pwa.update_available")}</div>
          <div className="mt-0.5 text-[12px] text-text-3">{t("pwa.update_hint")}</div>
        </div>
        <Button size="sm" onClick={() => updateServiceWorker(true)}>
          {t("pwa.reload")}
        </Button>
        <button
          type="button"
          aria-label={t("pwa.dismiss")}
          onClick={() => setNeedRefresh(false)}
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-3 transition-colors hover:bg-[color:var(--surface-2)]"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
